import React, { Component } from "react";
import { withLocalize } from "react-localize-redux";
import moment from "moment";
import { ContentContainer, SpinLoading } from "../../styles/BasicStyles";
import { Modal, Button } from "antd";
import Alert from "../../components/alert/Alert";
import {
  GetSharedOrganizerAgenda,
  GetMeetingAttendingTypes,
  CreateMeetingSchedule
} from "../../infra/requests/ScheduleRequests";
import MeetingOptionsFields, { MeetingLanguages } from "../agenda/MeetingOptionsFields";
import {
  Title,
  Subtitle,
  AgendaWrapper,
  AgendaTable,
  AgendaHead,
  OrganizerHead,
  OrganizerSubHead,
  DayCell,
  TimeCell,
  NoSlotsCell,
  BookButton,
  BookCell,
  EmptyAgenda
} from "./OrganizersAgendaStyles";

const DEFAULT_COLOUR = "#9AA89A";

/** Idiomas de atendimento que servem a cada idioma de reunião. */
const LANGUAGE_NEEDS = {
  pt: ["portuguese"],
  en: ["english"]
};

// Cada organizador ocupa duas colunas: a hora e o botão de marcar
const BLOCK_COLUMNS = 2;

/** Branco em cores escuras, preto em cores claras. */
const channelLuminance = value => {
  const channel = value / 255;
  return channel <= 0.03928
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);
};

const getTextColour = colour => {
  const hex = (colour || "").replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const luminance =
    0.2126 * channelLuminance(parseInt(hex.substr(0, 2), 16)) +
    0.7152 * channelLuminance(parseInt(hex.substr(2, 2), 16)) +
    0.0722 * channelLuminance(parseInt(hex.substr(4, 2), 16));
  return luminance < 0.5 ? "#ffffff" : "#000000";
};

/**
 * Agenda que um ou mais organizadores partilharam com o casal,
 * identificada pelo id da partilha que vem no link do email.
 *
 * Só chegam aqui os horários livres — o que está ocupado ou bloqueado não
 * vem sequer no payload —, por isso não há estados que mostrar: cada linha
 * é uma hora em que o casal pode marcar. Com mais do que um organizador,
 * as agendas aparecem lado a lado para se poderem comparar.
 */
class OrganizersAgendaPage extends Component {
  state = {
    ready: false,
    error: null,
    organizers: [],
    expiresAt: null,
    canBook: false,
    days: [],
    booking: null,
    saving: false,
    attendingTypes: [],
    attendingType: null,
    language: null
  };

  componentDidMount() {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
    document.body.style.overflowY = "auto";
    this.getAgenda();
    this.getAttendingTypes();
  }

  getAttendingTypes = async () => {
    const result = await GetMeetingAttendingTypes();
    this.setState({
      attendingTypes: result?.success && result?.data ? result.data : []
    });
  };

  /**
   * Opções ("Presencial"/"Online") cujo tipo concreto, no espaço do
   * casamento, o organizador escolhido faz.
   */
  organizerAttendingTypes = organizer => {
    const { attendingTypes } = this.state;
    const ids = (organizer?.attendingType || []).map(id => `${id}`);
    return attendingTypes.filter(m => ids.includes(`${m.attendingType}`));
  };

  /** Idiomas em que o organizador escolhido atende. */
  organizerLanguages = organizer => {
    const spoken = organizer?.attendingLanguages || [];
    return MeetingLanguages.filter(m =>
      LANGUAGE_NEEDS[m.code].some(lang => spoken.includes(lang))
    );
  };

  openBooking = booking => {
    // Com uma só opção não há escolha a fazer: fica logo seleccionada
    const types = this.organizerAttendingTypes(booking.organizer);
    const languages = this.organizerLanguages(booking.organizer);
    this.setState({
      booking,
      attendingType: types.length === 1 ? types[0]._id : null,
      language: languages.length === 1 ? languages[0].code : null
    });
  };

  getAgenda = async () => {
    const { match } = this.props;
    const result = await GetSharedOrganizerAgenda(match?.params?.id);

    if (!result?.success) {
      return this.setState({
        ready: true,
        error: result?.message === "SHARE_EXPIRED" ? "expired" : "notFound"
      });
    }

    this.setState({
      ready: true,
      error: null,
      organizers: result?.data?.organizers || [],
      expiresAt: result?.data?.expiresAt || null,
      canBook: result?.data?.canBook ? true : false,
      days: result?.data?.days || []
    });
  };

  bookMeeting = async () => {
    const { booking, attendingType, language } = this.state;
    if (!booking || !attendingType || !language) return;

    this.setState({ saving: true });
    const result = await CreateMeetingSchedule({
      date: booking.date,
      startTime: booking.time,
      organizer: booking.organizer?._id,
      attendingType,
      language
    });
    this.setState({ saving: false });

    if (result?.success) {
      Alert.new({
        type: "success",
        title: "Reunião marcada",
        text: "Vai receber um email com a confirmação."
      });
      this.setState({ booking: null }, this.getAgenda);
    } else {
      // O erro já é mostrado pelo interceptor; aqui só se fecha e recarrega,
      // porque a causa costuma ser o horário ter entretanto sido ocupado.
      this.setState({ booking: null }, this.getAgenda);
    }
  };

  /**
   * Um dia ocupa tantas linhas quantas as do organizador com mais horas
   * livres; os blocos mais curtos levam células vazias, para as linhas não
   * escorregarem entre colunas.
   */
  renderDay = day => {
    const { organizers, canBook } = this.state;
    const date = moment.utc(day.date);
    const weekend = date.isoWeekday() > 5;

    const blocks = organizers.map(organizer => ({
      organizer,
      slots: day.byOrganizer?.[organizer._id] || []
    }));

    const rowCount = blocks.reduce(
      (max, block) => Math.max(max, block.slots.length),
      1
    );

    const rows = [];
    for (let index = 0; index < rowCount; index++) {
      const cells = [];

      if (index === 0) {
        cells.push(
          <DayCell key="day" rowSpan={rowCount} weekend={weekend}>
            <span className="month">{date.locale("pt").format("MMM")}</span>
            <span className="number">{date.date()}</span>
            <span className="weekday">{date.locale("pt").format("ddd")}</span>
          </DayCell>
        );
      }

      blocks.forEach(({ organizer, slots }) => {
        const key = `${day.date}-${organizer._id}`;

        // Sem horas livres neste dia: uma só célula fundida
        if (slots.length === 0) {
          if (index === 0) {
            cells.push(
              <NoSlotsCell
                key={`${key}-none`}
                className="block-start"
                colSpan={BLOCK_COLUMNS}
                rowSpan={rowCount}>
                sem horários
              </NoSlotsCell>
            );
          }
          return;
        }

        const slot = slots[index];

        // Este organizador tem menos horas do que o dia: linhas vazias
        if (!slot) {
          cells.push(
            <td key={`${key}-empty-${index}`} className="block-start" />,
            <td key={`${key}-empty-book-${index}`} />
          );
          return;
        }

        cells.push(
          <TimeCell key={`${key}-time-${slot.time}`} className="block-start">
            {slot.time}
          </TimeCell>,
          <BookCell key={`${key}-book-${slot.time}`}>
            {canBook && (
              <BookButton
                onClick={() =>
                  this.openBooking({ date: day.date, time: slot.time, organizer })
                }>
                Marcar reunião
              </BookButton>
            )}
          </BookCell>
        );
      });

      rows.push(
        <tr
          key={`${day.date}-${index}`}
          className={index === rowCount - 1 ? "day-end" : ""}>
          {cells}
        </tr>
      );
    }

    return rows;
  };

  render() {
    const { ready, error, organizers, expiresAt, days, canBook, booking, saving } = this.state;
    const { attendingType, language } = this.state;
    const { translate, activeLanguage } = this.props;

    if (!ready) return <SpinLoading />;

    if (error) {
      return (
        <ContentContainer>
          <Title>Agenda do organizador</Title>
          <EmptyAgenda>
            {error === "expired"
              ? "Este link já expirou. Peça ao seu organizador para partilhar a agenda novamente."
              : "Esta agenda não está disponível."}
          </EmptyAgenda>
        </ContentContainer>
      );
    }

    return (
      <ContentContainer>
        <Title>
          {organizers.length === 1
            ? `Agenda de ${organizers[0]?.name}`
            : "Agenda dos organizadores"}
        </Title>
        <Subtitle>
          Horários disponíveis no período partilhado consigo.
          {organizers.length > 1 && " Escolha o organizador e a hora que lhe derem mais jeito."}
          {expiresAt && (
            <React.Fragment>
              {" "}Este link é válido até{" "}
              {moment.utc(expiresAt).local().format("DD/MM/YYYY [às] HH:mm")}.
            </React.Fragment>
          )}
        </Subtitle>

        {!canBook && (
          <Subtitle>
            Esta agenda é apenas para consulta: já tem uma reunião marcada ou
            ainda não está na fase de marcar reuniões.
          </Subtitle>
        )}

        {days.length === 0 ? (
          <EmptyAgenda>
            Não há horários disponíveis neste período. Fale com o seu
            organizador para combinarem outra data.
          </EmptyAgenda>
        ) : (
          <AgendaWrapper>
            <AgendaTable>
              <AgendaHead>
                <tr>
                  <th className="day-head" rowSpan={2}>
                    Dia
                  </th>
                  {organizers.map(organizer => {
                    const colour = organizer.color || DEFAULT_COLOUR;
                    return (
                      <OrganizerHead
                        key={organizer._id}
                        className="block-start"
                        colSpan={BLOCK_COLUMNS}
                        colour={colour}
                        textColour={getTextColour(colour)}>
                        {organizer.name}
                      </OrganizerHead>
                    );
                  })}
                </tr>
                <tr>
                  {organizers.map(organizer => {
                    const colour = organizer.color || DEFAULT_COLOUR;
                    return (
                      <React.Fragment key={organizer._id}>
                        <OrganizerSubHead className="block-start" colour={colour}>
                          Horário
                        </OrganizerSubHead>
                        <OrganizerSubHead colour={colour} />
                      </React.Fragment>
                    );
                  })}
                </tr>
              </AgendaHead>
              <tbody>{days.map(day => this.renderDay(day))}</tbody>
            </AgendaTable>
          </AgendaWrapper>
        )}

        <Modal
          visible={booking ? true : false}
          title="Confirmar reunião"
          maskClosable={false}
          onCancel={() => this.setState({ booking: null })}
          footer={[
            <Button
              key="back"
              onClick={() => this.setState({ booking: null })}>
              Cancelar
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={saving}
              disabled={saving || !attendingType || !language}
              onClick={this.bookMeeting}>
              Confirmar
            </Button>
          ]}>
          {booking && (
            <div>
              <p>
                Reunião com <b>{booking.organizer?.name}</b> em{" "}
                <b>
                  {moment.utc(booking.date).format("dddd, DD/MM/YYYY")} às{" "}
                  {booking.time}
                </b>
                .
              </p>
              <MeetingOptionsFields
                attendingTypes={this.organizerAttendingTypes(booking.organizer)}
                attendingType={attendingType}
                language={language}
                languages={this.organizerLanguages(booking.organizer)}
                disabled={saving}
                onChangeAttendingType={value => this.setState({ attendingType: value })}
                onChangeLanguage={value => this.setState({ language: value })}
                translate={translate}
                activeLanguage={activeLanguage}
              />
              <p style={{ color: "#6b7280", fontSize: 13 }}>
                Depois de confirmar receberá um email com os detalhes.
              </p>
            </div>
          )}
        </Modal>
      </ContentContainer>
    );
  }
}

export default withLocalize(OrganizersAgendaPage);
